import { clsx } from 'clsx';
import type { EditingHintHighlight, FormattingMark, FormattingMarkKind } from '@suvoda-uat/domain';
import { FORMATTING_MARK_KINDS, clampToBody, normalize } from '@suvoda-uat/domain';

/**
 * Read-only render of a Content Item body with its inline formatting and the
 * template editing-hint highlight (F-049). B/I/U render with intrinsic non-colour
 * cues — weight / slant / underline (FR-014). The editing-hint highlight renders
 * as a SINGLE colour-independent "needs-review" treatment that does NOT rely on
 * colour alone: a dotted underline cue + an accessible label (FR-013), the same
 * regardless of the template's source hue. Never used on the export path.
 */
export type MarksViewProps = {
  readonly body: string;
  readonly formatting?: readonly FormattingMark[];
  readonly editingHintHighlights?: readonly EditingHintHighlight[];
};

const KIND_CLASS: Readonly<Record<FormattingMarkKind, string>> = {
  bold: 'font-bold',
  italic: 'italic',
  underline: 'underline',
};

type ViewSegment = {
  readonly text: string;
  readonly kinds: readonly FormattingMarkKind[];
  readonly highlighted: boolean;
};

const toViewSegments = (
  body: string,
  formatting: readonly FormattingMark[],
  highlights: readonly EditingHintHighlight[],
): readonly ViewSegment[] => {
  const chars = Array.from(body);
  const length = chars.length;
  if (length === 0) {
    return [];
  }
  const marks = normalize(formatting, length);
  const hints = clampToBody(highlights, length);
  const boundaries = new Set<number>([0, length]);
  for (const mark of marks) {
    boundaries.add(mark.offset);
    boundaries.add(mark.offset + mark.length);
  }
  for (const hint of hints) {
    boundaries.add(hint.offset);
    boundaries.add(hint.offset + hint.length);
  }
  const points = [...boundaries].sort((a, b) => a - b);
  const segments: ViewSegment[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i] as number;
    const stop = points[i + 1] as number;
    segments.push({
      text: chars.slice(start, stop).join(''),
      kinds: FORMATTING_MARK_KINDS.filter((kind) =>
        marks.some(
          (mark) => mark.kind === kind && mark.offset <= start && start < mark.offset + mark.length,
        ),
      ),
      highlighted: hints.some((hint) => hint.offset <= start && start < hint.offset + hint.length),
    });
  }
  return segments;
};

export function MarksView({ body, formatting = [], editingHintHighlights = [] }: MarksViewProps) {
  const segments = toViewSegments(body, formatting, editingHintHighlights);
  if (segments.length === 0) {
    return <>{body}</>;
  }
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.kinds.length === 0 && !segment.highlighted) {
          // Plain run — emit a bare text node so an unformatted body is identical
          // to today's render (no wrapper spans, SC-005-friendly DOM).
          return <span key={index}>{segment.text}</span>;
        }
        return (
          <span
            key={index}
            {...(segment.highlighted
              ? { 'data-editing-hint': 'true', title: 'Template hint — likely needs review' }
              : {})}
            className={clsx(
              segment.kinds.map((kind) => KIND_CLASS[kind]),
              // Non-colour cue (dotted underline) + a colour-independent amber tint;
              // identical treatment regardless of the template's source colour (FR-013).
              segment.highlighted &&
                'bg-warning-bg/60 [text-decoration:underline_dotted] decoration-warning-fg',
            )}
          >
            {segment.text}
          </span>
        );
      })}
    </>
  );
}
